import React from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardHeaderActions,
  CardTitle,
  CardTitleCount,
  CardTabs,
} from 'components/Card'
import Icon from 'components/Icon'
import { observer } from 'mobx-react'
import JobViewModel from '../JobViewModel'
import { JobCardEmptyState } from '../JobCardEmptyState'
import DocumentList from 'documents/views/DocumentList'
import { DialogIllustration } from 'components/Dialog'
import {
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownScroll,
  DropdownTitle,
} from 'components/Dropdown'
import FileButton from 'components/FileButton'
import { IconButton } from 'components/IconButton'
import ConfirmSheet from 'components/ConfirmSheet'
import {
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
} from 'components/ModalSheet'
import { SectionHeaderAction } from 'components/Section'
import { useToggle } from 'hooks/state-hooks'
import styles from './DocumentsCard.sass'
import { TabBar, TabItem } from '../../../../../components/TabBar'
import { DocumentsSectionTab as Tab } from '../JobDocumentsViewModel'
import { match } from '../../../../../utils/pattern-match-util'
import { SupportingItemChecklist } from './SupportingItemChecklist'
import DocumentManualVerificationModal from '../DocumentManualVerificationModal'

interface Props {
  vm: JobViewModel
}

export default observer(function DocumentsCard({ vm }: Props) {
  React.useEffect(() => {
    vm.documentsVM.documentsTabController.change(Tab.Documents)
    if (vm.documentsVM.hasSupportingItems) {
      vm.documentsVM.documentsTabController.change(Tab.ClientChecklist)
    }
  }, [vm.documentsVM.checklist])
  return (
    <Card unconstrained>
      <CardHeader compact>
        <CardTitle cardLayout dataTest="documents-tab-item-new">
          Documentation
        </CardTitle>
        <CardHeaderActions>
          {vm.canViewDocs && vm.documentsVM.filteredDocuments.length > 0 && (
            <SectionHeaderAction
              tooltip="Download all documents"
              disabled={vm.documentsVM.downloadAll.pending}
              onClick={vm.documentsVM.downloadAll}
            >
              <Icon name="download" />
            </SectionHeaderAction>
          )}
          {vm.hasAnyDocPermission && vm.job.status !== 'CLOSED' && (
            <DocumentActionsDropdown vm={vm} />
          )}
        </CardHeaderActions>
      </CardHeader>
      {vm.documentsVM.hasSupportingItems && (
        <CardTabs bordered>
          <TabBar>
            <TabItem
              cardLayout
              dataTest="documents-tab-item-checklist"
              active={
                vm.documentsVM.documentsTabController.current.tab ===
                Tab.ClientChecklist
              }
              onClick={() =>
                vm.documentsVM.documentsTabController.change(
                  Tab.ClientChecklist,
                )
              }
            >
              Client checklist
            </TabItem>
            <TabItem
              cardLayout
              dataTest="documents-tab-item-documents"
              active={
                vm.documentsVM.documentsTabController.current.tab ===
                Tab.Documents
              }
              onClick={() =>
                vm.documentsVM.documentsTabController.change(Tab.Documents)
              }
            >
              Documents
            </TabItem>
          </TabBar>
        </CardTabs>
      )}
      <CardContent scroll listContent>
        {match(vm.documentsVM.documentsTabController.current.tab, {
          [Tab.ClientChecklist]: () => {
            return <SupportingItemChecklist vm={vm} />
          },
          [Tab.Documents]: () => {
            return vm.documentsVM.filteredDocuments.length > 0 ? (
              <DocumentList
                documentsVM={vm.documentsVM}
                selectable
                compact
                showIcon={false}
                isCardLayout
              />
            ) : (
              <JobCardEmptyState
                helpText={
                  vm.job.status === 'CLOSED'
                    ? 'This job is closed, and you cannot submit any documents at this time.'
                    : 'Add any documents that are related to the job here for easy access and storage.'
                }
              ></JobCardEmptyState>
            )
          },
        })}
        <ConfirmSheet
          yes="Delete Forever"
          danger
          state={vm.documentsVM.confirmDocumentDelete}
        >
          <ModalSheetContentBlock>
            <DialogIllustration
              src={require('assets/images/img_remove-collaborator.svg')}
              alt="Illustration of a women holding a phone with a crossed-out bubble above the phone."
            />
            <ModalSheetContentTitle>
              Are you sure you want to delete this file?
            </ModalSheetContentTitle>
            <ModalSheetContentDescription>
              This action cannot be undone. Are you sure that you want to
              permanently delete this document?
            </ModalSheetContentDescription>
          </ModalSheetContentBlock>
        </ConfirmSheet>
        <ConfirmSheet
          yes="Delete Forever"
          danger
          state={vm.documentsVM.confirmDocumentDeleteSelected}
        >
          <ModalSheetContentBlock>
            <DialogIllustration
              src={require('assets/images/img_remove-collaborator.svg')}
              alt="Illustration of a women holding a phone with a crossed-out bubble above the phone."
            />
            <ModalSheetContentTitle>
              Are you sure you want to delete these files?
            </ModalSheetContentTitle>
            <ModalSheetContentDescription>
              This action cannot be undone. Are you sure that you want to
              permanently delete these documents?
            </ModalSheetContentDescription>
          </ModalSheetContentBlock>
        </ConfirmSheet>
        <DocumentManualVerificationModal
          vm={vm.documentsVM.documentManualVerificationVM}
        />
      </CardContent>
    </Card>
  )
})

interface DocumentActionsDropdownProps {
  vm: JobViewModel
}

const DocumentActionsDropdown = observer(
  ({ vm }: DocumentActionsDropdownProps) => {
    const dropdownOpened = useToggle(false)
    const toggle = dropdownOpened.toggle

    const documentTypesForUser = vm.jobWorkspaceMember.hasPermission(
      'ADMIN_DOCUMENT_MANAGE',
    )
      ? vm.documentsVM.documentTypes.items
      : vm.documentsVM.documentTypesForCollaboratorType

    return (
      <Dropdown
        width="medium"
        isOpen={dropdownOpened.isEnabled}
        onRequestClose={dropdownOpened.disable}
        position="bottom-end"
        button={
          <div className={styles.moreButton} onClick={toggle}>
            <IconButton>
              {vm.documentsVM.filteredDocuments.length > 0 ? (
                <Icon name="more" />
              ) : (
                <Icon name="add" />
              )}
            </IconButton>
          </div>
        }
        dataTest="add-document-dropdown-new"
      >
        <DropdownScroll>
          <DropdownTitle>Add new</DropdownTitle>
          {vm.canUploadDocs && (
            <>
              {documentTypesForUser.map((type) => (
                <DropdownItem key={type.id} relative>
                  <FileButton
                    overlay
                    onChange={(files: FileList) => {
                      vm.documentsVM.stageFileAsType(files, type)
                      toggle()
                    }}
                    data-test={`upload-document-input-${type.name}`}
                  />
                  {type.name}
                </DropdownItem>
              ))}
              <DropdownItem relative>
                <FileButton
                  overlay
                  multiple={true}
                  onChange={(files: FileList) => {
                    vm.documentsVM.stageFiles(files)
                    toggle()
                  }}
                  data-test="upload-document-input-new"
                />
                Other documents
              </DropdownItem>
            </>
          )}
          {vm.canUploadDocs && (
            <>
              <DropdownDivider />
              <DropdownTitle>Actions</DropdownTitle>
              <DropdownItem
                onClick={() => {
                  vm.documentLinkingPickerVM.show()
                }}
                data-test="add-file-link-existing-dropdown-item-new"
              >
                Link existing
              </DropdownItem>
            </>
          )}

          {(vm.canDownloadDocs || vm.canRemoveDocs) &&
            vm.hasDocumentsSelected && (
              <>
                {vm.canDownloadDocs && (
                  <DropdownItem
                    relative
                    onClick={() => {
                      vm.documentsVM.downloadSelected()
                    }}
                  >
                    Download selected
                    <CardTitleCount>
                      ({vm.documentsVM.selectedDocuments.size})
                    </CardTitleCount>
                  </DropdownItem>
                )}
                {vm.canRemoveDocs && (
                  <DropdownItem
                    relative
                    onClick={() => {
                      vm.documentsVM.deleteSelected()
                    }}
                  >
                    Delete selected
                    <CardTitleCount>
                      ({vm.documentsVM.selectedDocuments.size})
                    </CardTitleCount>
                  </DropdownItem>
                )}
              </>
            )}
        </DropdownScroll>
      </Dropdown>
    )
  },
)
